import React, { useEffect, useState, memo, useCallback, useContext } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Typography, Stack, Tab, Tabs, CircularProgress } from '@mui/material';
import { Auth } from 'aws-amplify';
import { ArrowBack } from '@mui/icons-material';

import {
  getActiveIngredient,
  getActiveIngredientDocuments,
  validateActiveIngredientTrials,
  submitCommonNames
} from '../../api/ActiveIngredientCuration';

import styles from '../RegulatoryInformation/styles/ApplicationTableView.styles';
import { regionSourceMapping } from './constants';
import Store from '../../store';
import Actions from '../../store/actions';
import TabPanel from './components/TabPanel';
import IngredientCuration from './components/IngredientCuration';
import ProductsCuration from './components/ProductsCuration';

interface IProduct {
  id: number;
  application_number: string;
  common_name: string[] | null;
  files: {
    title: string;
    s3Path: string;
  }[];
  overlay_trials_count: number;
  qc_status: string;
}

interface IActiveIngredient {
  id: number;
  active_ingredient: string;
  active_ingredient_qc_status: string;
  base_trials_count: number;
  common_name: string[] | null;
  products: IProduct[];
  source: string;
  trails: any[];
}

interface ICommonName {
  title: string;
  isValidated: boolean;
  isValid: boolean;
  trialsFound: number;
  message: string;
  isLoading: boolean;
  trialId?: string;
}

const ActiveIngredient = () => {
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { dispatch } = useContext<any>(Store);

  const [allCommonNames, setAllCommonNames] = useState<Record<string, ICommonName[]>>({});
  const [openedLists, setOpenedLists] = useState<Record<string, boolean>>({});
  const [allProductDocuments, setAllProductDocuments] = useState<any>({});
  const [currentSelectedProduct, setCurrentSelectedProduct] = useState('');
  const [user, setUser] = useState<any>();
  const [tabIndex, setTabIndex] = useState(0);
  const [activeIngredientTrials, setActiveIngredientTrials] = useState<any>({});
  const [currentValidatedActiveIngredient, setCurrentValidatedActiveIngredient] =
    useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const defaultCommonName: ICommonName = {
    title: '',
    isValidated: false,
    isValid: false,
    trialsFound: 0,
    message: '',
    isLoading: false
  };

  const [currentActiveIngredient, setCurrentActiveIngredient] = useState<IActiveIngredient | null>(
    null
  );

  useEffect(() => {
    const updateCommonNamesAndOpenedLists = (activeIngredient: IActiveIngredient) => {
      const newCommonNames: any = {};
      const currentOpenedLists: any = {};

      const activeSubstanceKey = `${activeIngredient.active_ingredient}-${activeIngredient.source}`;

      newCommonNames[activeSubstanceKey] = activeIngredient?.common_name
        ? activeIngredient?.common_name?.map((name: string) => ({
            ...defaultCommonName,
            title: name,
            isValidated: activeIngredient?.active_ingredient_qc_status !== 'Qced w/o match',
            isValid: activeIngredient?.active_ingredient_qc_status !== 'Qced w/o match'
          }))
        : [{ ...defaultCommonName }];

      currentOpenedLists[activeSubstanceKey] = true;

      activeIngredient?.products?.forEach((product: any) => {
        currentOpenedLists[product.application_number] = false;

        newCommonNames[product.application_number] = product?.common_name
          ? product?.common_name?.map((name: any) => ({
              ...defaultCommonName,
              title: name.common_name,
              trialId: name.id,
              isValidated: product?.qc_status !== 'Qced w/o match',
              isValid: product?.qc_status !== 'Qced w/o match'
            }))
          : [{ ...defaultCommonName }];

        return newCommonNames;
      });

      setOpenedLists(currentOpenedLists);

      setAllCommonNames(newCommonNames);
    };

    const fetchAllDocuments = async (activeIngredient: IActiveIngredient) => {
      const documents: Record<string, any> = {};

      await Promise.allSettled(
        activeIngredient.products.map(async (product: any) => {
          try {
            const response = await getActiveIngredientDocuments(
              activeIngredient.source,
              product.application_number
            );

            if (response?.status === 200 && response?.data) {
              documents[product.application_number] = response.data;
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Failed to fetch documents for ${product.application_number}`, error);
          }
        })
      );

      setAllProductDocuments(documents);
    };

    const fetchActiveIngredient = async () => {
      try {
        setIsLoading(true);
        const response = await getActiveIngredient(id);

        if (response?.status !== 200 || !response?.data) {
          return;
        }

        const [activeIngredient] = response.data;

        setCurrentActiveIngredient(activeIngredient);
        updateCommonNamesAndOpenedLists(activeIngredient);
        fetchAllDocuments(activeIngredient);
        setCurrentSelectedProduct(activeIngredient.products[0]?.application_number);

        if (activeIngredient?.trials) {
          const trials = {
            [activeIngredient.active_ingredient]: activeIngredient?.trials?.ct
          };

          setActiveIngredientTrials(trials);
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error fetching active ingredients', error);
      } finally {
        setIsLoading(false);
      }
    };

    const fetchUser = async () => {
      const authUser = await Auth.currentAuthenticatedUser();
      setUser({
        curatorName: authUser.attributes['custom:user'],
        curatorEmail: authUser.attributes.email
      });
    };

    fetchActiveIngredient();
    fetchUser();
  }, [id]);

  const addNewCommonName = useCallback((key: string) => {
    setAllCommonNames(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), { ...defaultCommonName }]
    }));
  }, []);

  const removeCommonName = useCallback((index: number, key: string) => {
    setAllCommonNames(prev => ({
      ...prev,
      [key]: prev[key].filter((_: any, i: number) => i !== index)
    }));
  }, []);

  const validateCommonNames = useCallback(
    (key: string, type: 'application' | 'active_ingredient') => {
      const isProduct = type === 'application';
      const entity = isProduct
        ? currentActiveIngredient?.products?.find(p => p.application_number === key)
        : currentActiveIngredient;

      const qcStatus = isProduct
        ? (entity as IProduct)?.qc_status
        : currentActiveIngredient?.active_ingredient_qc_status;

      if (qcStatus === 'Qced w/o match') {
        return true;
      }

      const commonNames = allCommonNames[key];

      const hasEmptyCommonName = commonNames.some(
        (commonName: any) => commonName.title.trim().length === 0
      );

      if (hasEmptyCommonName) {
        dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Common name(s) cannot be empty.' }
        });

        return false;
      }

      const uniqCommonNames = new Set(
        commonNames.map((commonName: any) => commonName.title.trim().toLowerCase())
      );

      const hasDuplicateCommonName = uniqCommonNames.size !== commonNames.length;

      if (hasDuplicateCommonName) {
        dispatch({
          type: Actions.SET_ALERT,
          value: { status: true, message: 'Common name(s) should be unique.' }
        });
        return false;
      }

      const invalidCommonNames = commonNames
        .filter((commonName: any) => !commonName.isValidated || !commonName.isValid)
        ?.map((commonName: any) => commonName.title);

      if (invalidCommonNames.length > 0 && !isProduct) {
        dispatch({
          type: Actions.SET_ALERT,
          value: {
            status: true,
            message: `Common name(s) ${invalidCommonNames.join(', ')} are invalid or not validated.`
          }
        });
        return false;
      }

      if (isProduct) {
        const invalidTrialIds = commonNames
          .filter(
            (commonName: any) => !commonName?.trialId || commonName?.trialId?.trim()?.length === 0
          )
          ?.map((commonName: any) => commonName.title);

        if (invalidTrialIds.length > 0) {
          dispatch({
            type: Actions.SET_ALERT,
            value: {
              status: true,
              message: `Trial Id for common name(s) ${invalidTrialIds.join(', ')} cannot be empty.`
            }
          });
          return false;
        }
      }

      return true;
    },
    [currentActiveIngredient, allCommonNames]
  );

  const getCommonNamesPayload = useCallback(
    (key: string, type: 'application' | 'active_ingredient') => {
      const isProduct = type === 'application';
      const entity = isProduct
        ? currentActiveIngredient?.products?.find(p => p.application_number === key)
        : currentActiveIngredient;

      const trialsCount = isProduct
        ? (entity as IProduct)?.overlay_trials_count || 0
        : currentActiveIngredient?.base_trials_count || 0;

      const commonNames = allCommonNames[key] || [];
      const titles = isProduct
        ? commonNames.map((item: any) => {
            return {
              common_name: item.title,
              id: item.trialId || ''
            };
          })
        : commonNames.map((item: any) => item.title);
      const maxTrialsFound = Math.max(
        ...commonNames.map((item: any) => item.trialsFound),
        trialsCount
      );

      return {
        id: entity?.id,
        trials_count: maxTrialsFound,
        common_name: titles,
        curator_name: user?.curatorName,
        curator_email: user?.curatorEmail,
        qc_status: isProduct
          ? (entity as IProduct)?.qc_status
          : currentActiveIngredient?.active_ingredient_qc_status,
        curation_type: type
      };
    },
    [currentActiveIngredient, allCommonNames]
  );

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement>,
    key: string,
    type: 'application' | 'active_ingredient'
  ) => {
    e.preventDefault();

    const validationStatus = validateCommonNames(key, type);

    if (!validationStatus) {
      return;
    }

    const commonNamesPayload = getCommonNamesPayload(key, type);

    const response = await submitCommonNames(commonNamesPayload);

    if (response?.status === 200) {
      dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Common names submitted successfully.', color: 'success' }
      });
    } else {
      dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message: 'Failed to submit common names.' }
      });
    }
  };

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, index: number, key: string) => {
      const { value } = event.target;

      setAllCommonNames(prev => ({
        ...prev,
        [key]: prev[key].map((item, i) =>
          i === index
            ? {
                ...item,
                title: value,
                isValidated: false,
                isValid: false,
                trialsFound: 0,
                message: ''
              }
            : item
        )
      }));
    },
    []
  );

  const handleTrialIdChange = useCallback((value: string, index: number, key: string) => {
    setAllCommonNames(prev => ({
      ...prev,
      [key]: prev[key].map((item, i) =>
        i === index
          ? {
              ...item,
              trialId: value
            }
          : item
      )
    }));
  }, []);

  const updateOpenedLists = useCallback(
    (key: any) => {
      setOpenedLists(prev => ({
        ...prev,
        [key]: !prev[key]
      }));
    },
    [openedLists]
  );

  const isCommonNameValid = useCallback(
    (index: number, key: string, title: string) => {
      const sanitizedTitle = title?.trim()?.toLowerCase() || '';
      const isEmpty = sanitizedTitle.length === 0;
      const isDuplicate = allCommonNames[key]?.some(
        (item: any, i: number) =>
          (item.title.toLowerCase() === sanitizedTitle ||
            item.title.toLowerCase() === title.toLowerCase()) &&
          i !== index
      );

      let isError = false;
      let message = '';

      if (isEmpty) {
        isError = true;
        message = 'Common name cannot be empty';
      }

      if (isDuplicate) {
        isError = true;
        message = `Common name '${title}' already exists`;
      }

      if (!isError) return true;

      setAllCommonNames(prev => ({
        ...prev,
        [key]: prev[key].map((item, i) =>
          i === index ? { ...item, isValidated: true, isValid: false, message } : item
        )
      }));

      return false;
    },
    [allCommonNames]
  );

  const onValidate = async (index: number, key: string, title: string, type: string) => {
    if (!isCommonNameValid(index, key, title)) {
      return;
    }

    setAllCommonNames(prev => ({
      ...prev,
      [key]: prev[key].map((item, i) =>
        i === index
          ? {
              ...item,
              isLoading: true
            }
          : item
      )
    }));

    const validationType = type === 'Base' ? 'base_trials' : 'overlay_trials';

    const payload: any = {
      active_ingredient: title,
      source: currentActiveIngredient?.source,
      validation_type: validationType
    };

    if (type === 'Overlay') {
      payload.appl_num = key;
      payload.application_source = regionSourceMapping[currentActiveIngredient?.source || ''];
    }

    const response = await validateActiveIngredientTrials(payload);

    const isSuccess = response?.status === 200 && response?.data;
    const trialsFound = isSuccess ? response.data?.ct?.length || 0 : 0;
    const isValid = trialsFound > 0;
    const isActiveIngredient = type === 'Base';
    const trials = isSuccess && isActiveIngredient ? response.data?.ct : [];

    if (isActiveIngredient) {
      setActiveIngredientTrials((prev: any) => ({
        ...prev,
        [title]: trials
      }));
    }

    const message = isSuccess
      ? `Common name '${title}' is ${
          isValid ? '' : 'not '
        }valid. Found ${trialsFound} ${type.toLowerCase()} trials.`
      : `Error validating '${title}'`;

    setAllCommonNames(prev => {
      if (!prev[key]) return prev;

      const updatedItems = prev[key].map((item, i) =>
        i === index
          ? { ...item, isValidated: true, isLoading: false, isValid, trialsFound, message }
          : item
      );

      return { ...prev, [key]: updatedItems };
    });
  };

  const handleActiveIngredientQcStatusChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setCurrentActiveIngredient(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          active_ingredient_qc_status: value
        };
      });
    },
    []
  );

  const handleProductQcStatusChange = useCallback((event: any, key: string) => {
    const { value } = event.target;

    setCurrentActiveIngredient(prev =>
      prev
        ? {
            ...prev,
            products: prev.products.map(product =>
              product.application_number === key ? { ...product, qc_status: value } : product
            )
          }
        : prev
    );
  }, []);

  return (
    <Stack pt={10} pl={2}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <ArrowBack
          cursor='pointer'
          onClick={() => {
            history.push(`/active_ingredient_curation`);
          }}
        />
        <Typography sx={styles.Typography}>Active Ingredient Curation</Typography>
      </Stack>

      {isLoading ? (
        <Stack alignItems='center' mt={10}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack>
          <Tabs value={tabIndex} onChange={(_, newValue) => setTabIndex(newValue)}>
            <Tab label='Active Ingredient' />
            <Tab label='Products' />
          </Tabs>
          <TabPanel value={tabIndex} index={0}>
            <IngredientCuration
              activeIngredient={currentActiveIngredient}
              allCommonNames={allCommonNames}
              addNewCommonName={addNewCommonName}
              removeCommonName={removeCommonName}
              handleInputChange={handleInputChange}
              onValidate={onValidate}
              handleActiveIngredientQcStatusChange={handleActiveIngredientQcStatusChange}
              handleSubmit={handleSubmit}
              activeIngredientTrials={activeIngredientTrials}
              currentValidatedActiveIngredient={currentValidatedActiveIngredient}
              setCurrentValidatedActiveIngredient={setCurrentValidatedActiveIngredient}
            />
          </TabPanel>
          <TabPanel value={tabIndex} index={1}>
            <ProductsCuration
              activeIngredient={currentActiveIngredient}
              allCommonNames={allCommonNames}
              addNewCommonName={addNewCommonName}
              removeCommonName={removeCommonName}
              handleInputChange={handleInputChange}
              onValidate={onValidate}
              handleProductQcStatusChange={handleProductQcStatusChange}
              handleSubmit={handleSubmit}
              updateOpenedLists={updateOpenedLists}
              openedLists={openedLists}
              allProductDocuments={allProductDocuments}
              setCurrentSelectedProduct={setCurrentSelectedProduct}
              currentSelectedProduct={currentSelectedProduct}
              activeIngredientTrials={activeIngredientTrials}
              handleTrialIdChange={handleTrialIdChange}
            />
          </TabPanel>
        </Stack>
      )}
    </Stack>
  );
};

export default memo(ActiveIngredient);
